import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { ComponentRegistry } from './ComponentRegistry';
import { ADMIN_MAX_OFFSET } from './historyApi';
import type { AdminPagination } from './types';
import styles from './admin.module.css';

export interface Column<T> { label: string; render: (row: T) => ReactNode }
export function DataTable<T extends { id: string }>({ label, rows, columns, pagination, onPageChange, busy = false, paginationId, rowFilter }: {
  label: string; rows: T[]; columns: Column<T>[]; pagination?: AdminPagination | null;
  onPageChange?: (offset: number) => void; busy?: boolean; paginationId?: string; rowFilter?: (row: T) => boolean;
}): ReactElement {
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(rows.length / 25));
  const current = Math.min(page, pages - 1);
  const pageRows = pagination ? rows : rows.slice(current * 25, (current + 1) * 25);
  const visibleRows = rowFilter ? pageRows.filter(rowFilter) : pageRows;
  const controls = pagination ? <>
    <span>{rows.length ? `第 ${pagination.offset + 1}–${pagination.offset + rows.length} 条记录` : '本页 0 条记录'}{rowFilter && ` · 本页匹配 ${visibleRows.length} 条`}</span>
    <button type="button" disabled={busy || !onPageChange || pagination.offset === 0} onClick={() => onPageChange?.(Math.max(0, pagination.offset - pagination.limit))}>上一页</button>
    <span>第 {Math.floor(pagination.offset / pagination.limit) + 1} 页</span>
    <button type="button" disabled={busy || !onPageChange || !pagination.hasMore || pagination.offset + pagination.limit > ADMIN_MAX_OFFSET} onClick={() => onPageChange?.(pagination.offset + pagination.limit)}>下一页</button>
  </> : <>
    <span>共 {rows.length} 条记录{rowFilter && ` · 本页匹配 ${visibleRows.length} 条`}</span><button type="button" disabled={busy || current === 0} onClick={() => setPage(current - 1)}>上一页</button><span>{current + 1} / {pages}</span><button type="button" disabled={busy || current + 1 >= pages} onClick={() => setPage(current + 1)}>下一页</button>
  </>;
  return <>
    <div className={styles.tableScroll} tabIndex={0} aria-label={`${label}表格`}>
      <table><caption className={styles.srOnly}>{label}</caption><thead><tr>{columns.map((column) => <th scope="col" key={column.label}>{column.label}</th>)}</tr></thead>
        <tbody>{visibleRows.length === 0 ? <tr><td colSpan={columns.length} className={styles.empty}>{rowFilter ? '本页无匹配记录' : '暂无记录'}</td></tr> : visibleRows.map((row) => <tr key={row.id}>{columns.map((column) => <td key={column.label} data-label={column.label}>{column.render(row)}</td>)}</tr>)}</tbody>
      </table>
    </div>
    {paginationId ? <ComponentRegistry id={paginationId} className={styles.pagination}>{controls}</ComponentRegistry> : <div className={styles.pagination}>{controls}</div>}
  </>;
}
